import React, { useMemo, useState, useEffect, useRef } from 'react';
import { indexById, canMove, buildTree, flattenTree } from '../../../services/docsTree';
import './MoveDocDialog.css';

/* Move a doc to a new parent.
   Illegal targets (the doc itself and its own descendants) are FILTERED OUT
   rather than shown-and-disabled: there's no useful reason to display a
   destination that would detach the branch, and canMove is re-checked on
   confirm so the guard doesn't depend on this list being right. */
function MoveDocDialog({ doc, docs, onCancel, onMove }) {
	const [selected, setSelected] = useState(null); // null === top level
	const panelRef = useRef(null);

	const byId = useMemo(() => indexById(docs), [docs]);

	const options = useMemo(() => {
		const legal = docs.filter((d) => canMove(doc.id, d.id, byId).ok);
		return flattenTree(buildTree(legal));
	}, [docs, doc.id, byId]);

	const currentParent = doc.parentId || null;

	useEffect(() => {
		const onKey = (e) => {
			if (e.key === 'Escape') onCancel();
		};
		document.addEventListener('keydown', onKey);
		panelRef.current?.focus();
		return () => document.removeEventListener('keydown', onKey);
	}, [onCancel]);

	return (
		<div className='move-dialog__backdrop' onClick={onCancel}>
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
			<div
				className='move-dialog'
				role='dialog'
				aria-modal='true'
				aria-label={`Move ${doc.title || 'Untitled'}`}
				tabIndex={-1}
				ref={panelRef}
				onClick={(e) => e.stopPropagation()}>
				<h2 className='move-dialog__title'>
					Move “{doc.title || 'Untitled'}”
				</h2>
				<p className='move-dialog__hint'>
					Any sub-pages move with it.
				</p>

				<ul className='move-dialog__list'>
					<li>
						<button
							type='button'
							className={`move-option ${selected === null ? 'is-selected' : ''}`}
							onClick={() => setSelected(null)}>
							<i className='fa-solid fa-house' aria-hidden='true'></i>
							<span>Top level</span>
							{currentParent === null && (
								<span className='move-option__current'>current</span>
							)}
						</button>
					</li>
					{options.map((o) => (
						<li key={o.id}>
							<button
								type='button'
								className={`move-option ${selected === o.id ? 'is-selected' : ''}`}
								style={{ '--depth': o.depth }}
								onClick={() => setSelected(o.id)}>
								<i className='fa-solid fa-file-lines' aria-hidden='true'></i>
								<span>{o.title || 'Untitled'}</span>
								{currentParent === o.id && (
									<span className='move-option__current'>current</span>
								)}
							</button>
						</li>
					))}
				</ul>

				<div className='move-dialog__actions'>
					<button type='button' className='move-dialog__cancel' onClick={onCancel}>
						Cancel
					</button>
					<button
						type='button'
						className='move-dialog__confirm'
						disabled={selected === currentParent}
						onClick={() => onMove(selected)}>
						{selected === currentParent ? 'Already here' : 'Move'}
					</button>
				</div>
			</div>
		</div>
	);
}

export default MoveDocDialog;
